import React from "react";
import { Box, Container, Typography, styled, Paper, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  HeroSection,
  HeroTitle,
  HeroSubtitle,
} from "../../../components/hero-section/HeroSection";

const ContentPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: "#fff",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3),
  },
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  "&:last-child": {
    marginBottom: 0,
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 600,
  marginBottom: theme.spacing(2),
}));

const EmailLink = styled("a")(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: "none",
  "&:hover": {
    textDecoration: "underline",
  },
}));

const ReturnsPolicy = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';
  const email = "support@hasabookstore.com";

  return (
    <Box>
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <HeroTitle>{t('help.returnsPolicy.title')}</HeroTitle>
              <HeroSubtitle>
                {t('help.returnsPolicy.subtitle')}
              </HeroSubtitle>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      <Box sx={{ py: 6, backgroundColor: "background.default" }}>
        <Container maxWidth="lg">
          <ContentPaper elevation={0}>
            <Section>
              <Typography variant="body1" paragraph>
                {t('help.returnsPolicy.intro')}
              </Typography>
            </Section>

            <Section>
              <SectionTitle variant="h5">{t('help.returnsPolicy.sections.access.title')}</SectionTitle>
              <Typography variant="body1" paragraph>
                {t('help.returnsPolicy.sections.access.content')}
              </Typography>
            </Section>

            <Section>
              <SectionTitle variant="h5">{t('help.returnsPolicy.sections.technicalIssues.title')}</SectionTitle>
              <Typography variant="body1" paragraph>
                {t('help.returnsPolicy.sections.technicalIssues.content', { email: email })}
              </Typography>
            </Section>

            <Section>
              <SectionTitle variant="h5">
                {t('help.returnsPolicy.sections.quality.title')}
              </SectionTitle>
              <Typography variant="body1" paragraph>
                {t('help.returnsPolicy.sections.quality.intro')}
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.returnsPolicy.sections.quality.items.formatting')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.returnsPolicy.sections.quality.items.complete')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.returnsPolicy.sections.quality.items.defects')}
                </Typography>
              </Box>
            </Section>

            <Section>
              <SectionTitle variant="h5">{t('help.returnsPolicy.sections.refund.title')}</SectionTitle>
              <Typography variant="body1" paragraph>
                {t('help.returnsPolicy.sections.refund.intro')}
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.returnsPolicy.sections.refund.items.technical')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.returnsPolicy.sections.refund.items.different')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.returnsPolicy.sections.refund.items.duplicate')}
                </Typography>
              </Box>
            </Section>

            <Section>
              <SectionTitle variant="h5">{t('help.returnsPolicy.sections.process.title')}</SectionTitle>
              <Typography variant="body1" paragraph>
                {t('help.returnsPolicy.sections.process.intro')}
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.returnsPolicy.sections.process.items.contact')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.returnsPolicy.sections.process.items.provide')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.returnsPolicy.sections.process.items.review')}
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  {t('help.returnsPolicy.sections.process.items.processed')}
                </Typography>
              </Box>
            </Section>

            <Section>
              <SectionTitle variant="h5">{t('help.returnsPolicy.sections.support.title')}</SectionTitle>
              <Typography variant="body1" paragraph>
                {t('help.returnsPolicy.sections.support.content', { email: email })}
              </Typography>
            </Section>
          </ContentPaper>
        </Container>
      </Box>
    </Box>
  );
};

export default ReturnsPolicy;
